import { Component, Input, forwardRef, ChangeDetectionStrategy, Output, EventEmitter, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, finalize, map, publishReplay, refCount, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import { CovanceSelectComponent } from '../../../covance-select/covance-select.component';
import { UserEmailsStorage } from './user-emails-storage';
import { EQueryAccessionInfo } from '../../shared/e-query-accession-info';

@Component({
    selector: 'user-emails-search',
    template: require('./user-emails-search.component.html'),
    styles: [require('./user-emails-search.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UserEmailsSearchComponent),
            multi: true
        }
    ]
})
export class UserEmailsSearchComponent implements ControlValueAccessor {

    public get value() {
        return this.val;
    }

    @Input()
    public set value(inputVal) {
        this.val = inputVal;
        this.onChange(this.val);
    }

    @Output()
    public selectedItemChanged = new EventEmitter<string>();

    @Input()
    public disabled = false;

    @Input()
    public thresholdSearch = 3;

    @Input()
    public set accessionNumber(value: string) {
        this.accessionNumber$.next(value);
    }
    
    public readonly searchFlow$ = new BehaviorSubject<string>(undefined);
    public readonly isSearching$ = new BehaviorSubject(false);
    public emails$: Observable<string[]>;
    public selectedItem$ = new BehaviorSubject<string>('');
    public selectedItems$: Observable<string[]>;

    private val: string;
    @ViewChild(CovanceSelectComponent)
    private covanceSelectComponent: CovanceSelectComponent;

    private accessionNumber$ = new BehaviorSubject<string>(undefined);

    constructor(private readonly userEmailsStorage: UserEmailsStorage) {
        this.emails$ = combineLatest(this.searchFlow$, this.accessionNumber$.pipe(filter(a => !!a))).pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() =>  {
                this.isSearching$.next(true);
            }),
            switchMap(([searchTerm, accessionNumber]) => this.loadEscalations(searchTerm, accessionNumber).pipe(
                finalize(() => this.isSearching$.next(false)),
            )),
        );

        this.selectedItems$ = this.emails$.pipe(
            withLatestFrom(this.selectedItem$),
            tap(([emails, selectedEmail]) =>  {
                this.onSelectedEmailChange([selectedEmail]);
            }),
            map(([emails, selectedEmail]) => {      
                return emails.filter(v =>  v === selectedEmail);
            }),
            publishReplay(1),
            refCount());
    }

    public onChange: any = () => { };
    public onTouched: any = () => { };

    public searchTermChanged(term: string) {
        if (!!term) {
            this.searchFlow$.next(term);
        }
    }

    public onSelectedEmailChange(emailSelects: string[]) {
        const emailSelect = emailSelects[0];
        if (emailSelect) {
            this.selectedItemChanged.emit(emailSelect);
            this.value = emailSelect;
            this.onChange(emailSelect);
        }
    }

    public writeValue(value: string): void {
        this.value = value;
    }

    public clearValue(): void {
        this.searchFlow$.next(null);
        this.covanceSelectComponent.clearSelectedItems();
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private loadEscalations(searchTerm: string, accessionNumber: string): Observable<string[]> {
        return searchTerm && searchTerm.length < this.thresholdSearch
            ? of([])
            : this.userEmailsStorage.search(searchTerm, accessionNumber);
    }
}
